import { useAlert } from 'context/alert';
import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Info, AlertTriangle, X, ChevronDown, ChevronUp } from 'lucide-react';

/* Icon + accent colour per alert type */
const TOAST_CONFIG = {
  success: {
    icon: CheckCircle,
    bg:   '#f0fdf4',
    border:'#bbf7d0',
    accent:'#15803d',
    title: 'Success',
  },
  danger: {
    icon: XCircle,
    bg:   '#fef2f2',
    border:'#fecaca',
    accent:'#b91c1c',
    title: 'Error',
  },
  error: {
    icon: XCircle,
    bg:   '#fef2f2',
    border:'#fecaca',
    accent:'#b91c1c',
    title: 'Error',
  },
  warning: {
    icon: AlertTriangle,
    bg:   '#fffbeb',
    border:'#fde68a',
    accent:'#b45309',
    title: 'Warning',
  },
  info: {
    icon: Info,
    bg:   '#eff6ff',
    border:'#bfdbfe',
    accent:'#1d4ed8',
    title: 'Info',
  },
};

const Alert = () => {
  const { alert, hideAlert } = useAlert();
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    setShowDetails(false);
  }, [alert]);

  if (!alert.show) return null;

  const config = TOAST_CONFIG[alert.type] || TOAST_CONFIG.info;
  const IconComponent = config.icon;

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: '16px',
        right: '16px',
        zIndex: 9999,
        minWidth: '300px',
        maxWidth: '380px',
        display: 'flex',
        flexDirection: 'column',
        background: config.bg,
        border: `1px solid ${config.border}`,
        borderLeft: `4px solid ${config.accent}`,
        borderRadius: '8px',
        boxShadow: '0 8px 28px rgba(15, 23, 42, 0.14), 0 2px 8px rgba(15, 23, 42, 0.06)',
        overflow: 'hidden',
        animation: 'toastSlideIn .22s cubic-bezier(0.22, 1, 0.36, 1)',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      }}
    >
      {/* Main content row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', padding: '12px 14px', gap: '10px' }}>
        {/* Icon */}
        <span style={{ flexShrink: 0, marginTop: '1px' }}>
          <IconComponent size={16} color={config.accent} />
        </span>

        {/* Text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#0f172a', lineHeight: 1.4 }}>
            {config.title}
          </p>
          <p style={{ margin: '2px 0 0', fontSize: '13px', fontWeight: 400, color: '#374151', lineHeight: 1.5, wordBreak: 'break-word' }}>
            {alert.message}
          </p>

          {/* Expandable detail */}
          {alert.detail && (
            <>
              <button
                onClick={() => setShowDetails(!showDetails)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3px',
                  marginTop: '6px',
                  padding: 0,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: config.accent,
                }}
              >
                {showDetails ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                {showDetails ? 'Less detail' : 'More detail'}
              </button>
              {showDetails && (
                <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#475569', lineHeight: 1.5, fontFamily: 'monospace' }}>
                  {alert.detail}
                </p>
              )}
            </>
          )}
        </div>

        {/* Close button */}
        <button
          onClick={hideAlert}
          aria-label="Close notification"
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '22px',
            height: '22px',
            padding: 0,
            borderRadius: '50%',
            background: 'rgba(0,0,0,.06)',
            border: 'none',
            cursor: 'pointer',
            color: '#64748b',
            transition: 'background .12s ease',
            marginTop: '1px',
          }}
        >
          <X size={12} />
        </button>
      </div>

      {/* Inline keyframes via style tag */}
      <style>{`
        @keyframes toastSlideIn {
          from { opacity: 0; transform: translateX(24px) scale(0.96); }
          to   { opacity: 1; transform: translateX(0)   scale(1);    }
        }
      `}</style>
    </div>
  );
};

export default Alert;
